import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../core/design-system/components/Icon';
import { Spinner } from '../../../../core/design-system/components/Spinner';
import { useCases } from '../../../../core/di/useCases';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { useFollowRequests } from '../../hooks/useFollowRequests';
import UserListItem from './UserListItem';
import './styles.css';

const FollowersModal = ({ isOpen, onClose, userId, type, onRequestsChange }) => {
  const { t } = useTranslation('profile');
  const { user: currentUser } = useAuth();
  const { acceptFollowRequest, rejectFollowRequest } = useFollowRequests();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(type || 'followers');
  const [processingIds, setProcessingIds] = useState([]);
  const isOwnProfile = currentUser?.uid === userId;

  // Fermer la modal avec la touche Echap
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [isOpen, onClose]);

  // Chargement des utilisateurs selon l'onglet actif
  useEffect(() => {
    const loadUsers = async () => {
      if (!userId || !isOpen) return;
      
      setLoading(true);
      try {
        const userProfile = await useCases.getUserProfile.execute(userId);
        let userIds = [];
        
        switch (activeTab) {
          case 'following':
            userIds = userProfile.following || [];
            break;
          case 'followers':
            userIds = userProfile.followers || [];
            break;
          case 'requests':
            userIds = userProfile.followRequests || [];
            break;
          default:
            userIds = [];
        }
        
        // Récupérer les détails de chaque utilisateur
        const usersData = [];
        for (const id of userIds) {
          try {
            const user = await useCases.getUserProfile.execute(id);
            usersData.push(user);
          } catch (error) {
            console.error(`Erreur lors du chargement de l'utilisateur ${id}:`, error);
          }
        }
        
        setUsers(usersData);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUsers();
  }, [userId, isOpen, activeTab]);

  // Gérer l'acceptation d'une demande
  const handleAccept = async (requesterId) => {
    try {
      setProcessingIds(prev => [...prev, requesterId]);
      await acceptFollowRequest(requesterId);
      setUsers(prev => prev.filter(user => user.uid !== requesterId));
      onRequestsChange?.();
    } catch (error) {
      console.error('Erreur lors de l\'acceptation:', error);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== requesterId));
    }
  };

  // Gérer le rejet d'une demande
  const handleReject = async (requesterId) => {
    try {
      setProcessingIds(prev => [...prev, requesterId]);
      await rejectFollowRequest(requesterId);
      setUsers(prev => prev.filter(user => user.uid !== requesterId));
      onRequestsChange?.();
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== requesterId));
    }
  };

  // Gérer le désabonnement
  const handleUnfollow = async (targetId) => {
    try {
      setProcessingIds(prev => [...prev, targetId]);
      await useCases.unfollowUser.execute(currentUser.uid, targetId);
      setUsers(prev => prev.filter(user => user.uid !== targetId));
    } catch (error) {
      console.error('Erreur lors du désabonnement:', error);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== targetId));
    }
  };

  // Gérer la suppression d'un follower
  const handleRemoveFollower = async (followerId) => {
    try {
      setProcessingIds(prev => [...prev, followerId]);
      await useCases.removeFollower.execute(currentUser.uid, followerId);
      setUsers(prev => prev.filter(user => user.uid !== followerId));
    } catch (error) {
      console.error('Erreur lors de la suppression du follower:', error);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== followerId));
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const getTitle = () => {
    switch (activeTab) {
      case 'following':
        return t('modal.followingTitle');
      case 'followers':
        return t('modal.followersTitle');
      case 'requests':
        return t('modal.requestsTitle');
      default:
        return '';
    }
  };

  const getEmptyMessage = () => {
    switch (activeTab) {
      case 'following':
        return t('emptyState.noFollowing');
      case 'followers':
        return t('emptyState.noFollowers');
      case 'requests':
        return t('emptyState.noRequests');
      default:
        return '';
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="follow-modal">
        <div className="modal-header">
          <h2 className="modal-title">{getTitle()}</h2>
          <button 
            type="button"
            className="close-button" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <Icon name="close" size={24} />
          </button>
          
          {isOwnProfile && (
            <div className="tabs">
              <button
                type="button"
                className={`tab ${activeTab === 'followers' ? 'active' : ''}`}
                onClick={() => setActiveTab('followers')}
              >
                {t('modal.followersTitle')}
              </button>
              <button
                type="button"
                className={`tab ${activeTab === 'following' ? 'active' : ''}`}
                onClick={() => setActiveTab('following')}
              >
                {t('modal.followingTitle')}
              </button>
              <button
                type="button"
                className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                {t('modal.requestsTitle')}
              </button>
            </div>
          )}
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-container">
              <Spinner size="md" />
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <p>{getEmptyMessage()}</p>
            </div>
          ) : (
            <ul className="users-list">
              {users.map(user => (
                <UserListItem
                  key={user.uid}
                  user={user}
                  activeTab={activeTab}
                  isOwnProfile={isOwnProfile}
                  processingIds={processingIds}
                  handleAccept={handleAccept}
                  handleReject={handleReject}
                  handleUnfollow={handleUnfollow}
                  handleRemoveFollower={handleRemoveFollower}
                  t={t}
                  onClose={onClose}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowersModal; 